import { Component } from '@angular/core';
import Highcharts from 'highcharts';
import { FILTERED_SERIES_NAME, paletteColors } from '../report-aggregation';
import { ChartBlockBase } from './chart-block-base';
import {
  CHART_SPACING,
  CHART_V_SPACING,
  COLUMN_PLOT_COMPACT_PX,
  COLUMN_PLOT_PX,
  legendHeightFor,
  legendRowsFor,
  axisMaxFor,
  columnValueAxis,
  categoryLabelFormatter,
  categoryLabelStyle,
  clampedCategories,
  BAR_BORDER_RADIUS,
  BAR_GAP_PX,
  BAR_LABEL_GUTTER_PX,
  BAR_THICKNESS_PX,
  barLabelColumn,
  barPointWidth,
  categoryLabelPlacement,
  lineHeightFor,
  endLabelDataLabels,
  endLabelMargins,
  categoryAxis,
  hiddenPercentAxis,
  isHorizontal,
  labelFitFor,
} from './chart-layout';

@Component({
  selector: 'app-categorical-distribution-block',
  standalone: false,
  template: `
    @if (chartOptions) {
      <!-- Rebuilt, not updated, when the chart is turned: Highcharts decides whether a chart is
         on its side before it changes the series' type, so an update from bars to columns is
         judged against the bars still there and the chart stays sideways. -->
      @for (turn of [orientation]; track turn) {
        <highcharts-chart [Highcharts]="highcharts" [oneToOne]="true" [options]="chartOptions"
                          style="width: 100%; display: block;"></highcharts-chart>
      }
    }
  `,
  // Inline by default, which would make the width measured in ChartBlockBase 0.
  styles: [':host { display: block; }']
})
export class CategoricalDistributionBlockComponent extends ChartBlockBase {
  highcharts = Highcharts;
  chartOptions: Highcharts.Options | null = null;
  orientation: 'horizontal' | 'vertical' = 'horizontal';

  protected build() {
    if (this.block.reportType !== 'filter-comparison') return;
    const horizontal = isHorizontal(this.block.reportType, this.block.reportExtraData.orientation);
    this.orientation = horizontal ? 'horizontal' : 'vertical';
    const categories = this.series[0]?.points.map(p => p.label) ?? [];

    const axisMax = axisMaxFor(this.series);
    const rows = Math.max(categories.length, this.alignCount ?? 0);

    const roles = paletteColors(this.palette);
    const labelSize = this.labelSize();
    const fontFamily = this.fontFamily();
    const lineHeight = lineHeightFor(labelSize);
    const labelColumn = barLabelColumn(this.plotWidth());
    const fit = labelFitFor({
      plotWidth: this.plotWidth(),
      slots: rows,
      categories,
      labelSize,
      horizontal,
      horizontalWidth: labelColumn - BAR_LABEL_GUTTER_PX,
      fontFamily,
      // One line, always -- see the bar block: the row pitch is fixed, so a wrapped label would
      // have to push the rows apart. Cut instead; the full text stays the category.
      ...(horizontal ? { maxLines: 1 } : {}),
    });

    // Cut to the lines reserved below, or a long answer is drawn over the category under it.
    const labelText = clampedCategories(categories, fit, labelSize, fontFamily);

    // Two bars share each category slot, so a row is two of the deck's bars plus the gap under
    // them -- same thickness and same gap as every other bar chart, just twice over, and flat
    // whatever the labels are doing.
    const hideLabels = !!this.block.reportExtraData.hideLabels;
    const rowHeight = horizontal ? 2 * BAR_THICKNESS_PX + BAR_GAP_PX : 52;
    // Horizontal reclaims the space the hidden % axis used to need at the bottom. Vertical keeps its
    // category labels down there either way, and needs room for the lines they wrap to.
    // Measured rather than guessed -- see CHART_V_SPACING. The legend is always on here, so its
    // height is always part of what sits outside the plot.
    const legendCost = legendHeightFor(
      legendRowsFor(this.series.map(s => s.name), this.plotWidth(), fontFamily));
    const chrome = horizontal
      ? CHART_V_SPACING + legendCost
      : (hideLabels ? 76 : 130 + (fit.lines - 1) * lineHeight);

    const type = horizontal ? 'bar' : 'column';

    this.chartOptions = this.deckMotion({
      chart: {
        type,
        // No floor on a horizontal chart. Padding the height out to a minimum does not make the
        // bars bigger -- thickness is a flat pointWidth now -- so every spare pixel lands in the
        // gaps between rows instead. A four-row chart was being stretched to 280px and drawing its
        // 15px bars with 40px of air between them. A column chart takes a fixed plot instead: its
        // answers run across, so they set the columns' width and not the chart's height.
        height: horizontal
          ? rows * rowHeight + chrome
          : (this.compact ? COLUMN_PLOT_COMPACT_PX : COLUMN_PLOT_PX) + chrome,
        spacing: CHART_SPACING,
        ...endLabelMargins(horizontal),
        // Set either way, never left out: see categoryLabelPlacement. Absent, the label column
        // a horizontal chart reserves stays reserved once the chart is turned upright.
        marginLeft: horizontal && !hideLabels ? labelColumn : undefined,
        style: { fontFamily: 'inherit' },
      },
      title: { text: undefined },
      credits: { enabled: false },
      // Always on, unlike the bar block's series-count rule: which bar is the whole sample and which
      // is the filter is the entire content of this chart, so it can never be left to be inferred.
      legend: { enabled: true, itemStyle: { fontSize: '11px', fontWeight: '400' } },
      xAxis: categoryAxis({
        categories,
        labels: {
          step: 1,
          formatter: categoryLabelFormatter(labelText),
          ...categoryLabelPlacement(horizontal, labelColumn),
          style: {
            ...categoryLabelStyle(fit, labelSize),
            textAlign: horizontal ? 'left' : 'center',
          },
        },
      }, !hideLabels),
      yAxis: horizontal ? hiddenPercentAxis(axisMax) : columnValueAxis(!!this.compact),
      tooltip: { pointFormat: '{series.name}: <b>{point.y:.0f}%</b> ({point.custom.count})' },
      plotOptions: {
        // The two series are both real and have to sit side by side to be read against each other,
        // hence tighter point padding than the bar block: two bars share every category slot.
        // A bar begins exactly on the plot's left edge, so the tip of its rounded start corner
        // falls on the clip boundary and gets shaved -- that end then reads as squarer than the
        // other. clip:false removes the boundary; nothing can overflow it anyway, since the axis
        // maximum is derived from the data.
        series: { borderWidth: 0, clip: false },
        bar: {
          borderRadius: BAR_BORDER_RADIUS,
          grouping: true,
          pointPadding: 0.02,
          groupPadding: 0.18,
          ...barPointWidth(rowHeight, 2),
        },
        column: {
          borderRadius: BAR_BORDER_RADIUS,
          grouping: true, pointPadding: 0.02, groupPadding: 0.18,
        },
      },
      series: this.series.map(s => {
        // Emphasis goes to the series the reader is actually moving. distributionSeries() returns
        // [Total Overall, Dynamic to Filter], so the filtered one takes the palette's primary and
        // the whole-sample reference sits behind it in secondary.
        const role = s.name === FILTERED_SERIES_NAME ? 0 : 1;
        return {
          type,
          name: s.name,
          colorIndex: role,
          color: roles[role % roles.length],
          dataLabels: endLabelDataLabels(),
          data: s.points.map(p => ({ y: p.percent, custom: { count: p.value } })),
        };
      }) as Highcharts.SeriesOptionsType[],
    });
  }
}
